import React, { useCallback, useEffect, useRef, useState } from "react";
import { Iconography, IconographySizes } from "@hid-galaxy-ui/galaxy-react";
import "../calculator/Calculator.scss";

export interface AssessmentDayPickerProps {
  /** ISO date string `YYYY-MM-DD` (empty when unset) */
  value: string;
  onChange: (isoDate: string) => void;
  disabled?: boolean;
}

const getMonthName = (month: number) => {
  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  return months[month];
};

/**
 * Same date field + custom calendar as Partner Program calculator tab (assessment day).
 */
const AssessmentDayPicker: React.FC<AssessmentDayPickerProps> = ({
  value,
  onChange,
  disabled = false,
}) => {
  const assessmentDatePickerRef = useRef<HTMLDivElement>(null);
  const [showCalendar, setShowCalendar] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState(() => {
    if (value) {
      const [y, m] = value.split("-").map(Number);
      if (y && m) return m - 1;
    }
    return new Date().getMonth();
  });
  const [calendarYear, setCalendarYear] = useState(() => {
    if (value) {
      const [y] = value.split("-").map(Number);
      if (y) return y;
    }
    return new Date().getFullYear();
  });

  useEffect(() => {
    if (!value) return;
    const [y, m] = value.split("-").map(Number);
    if (y && m) {
      setCalendarMonth(m - 1);
      setCalendarYear(y);
    }
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        assessmentDatePickerRef.current &&
        !assessmentDatePickerRef.current.contains(event.target as Node) &&
        showCalendar
      ) {
        setShowCalendar(false);
      }
    };

    if (showCalendar) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showCalendar]);

  const handleCalendarToggle = () => {
    if (disabled) return;
    if (!showCalendar && value) {
      const [y, m] = value.split("-").map(Number);
      if (y && m) {
        setCalendarMonth(m - 1);
        setCalendarYear(y);
      }
    }
    setShowCalendar((open) => !open);
  };

  const handleDateSelect = (
    day: number,
    isCurrentMonth: boolean,
    isDisabled?: boolean
  ) => {
    if (isDisabled || disabled) return;

    let selectedMonth = calendarMonth;
    let selectedYear = calendarYear;

    if (!isCurrentMonth) {
      if (day > 20) {
        selectedMonth = calendarMonth - 1;
        if (selectedMonth < 0) {
          selectedMonth = 11;
          selectedYear -= 1;
        }
      } else {
        selectedMonth = calendarMonth + 1;
        if (selectedMonth > 11) {
          selectedMonth = 0;
          selectedYear += 1;
        }
      }
    }

    const month = String(selectedMonth + 1).padStart(2, "0");
    const formattedDay = String(day).padStart(2, "0");
    const dbDate = `${selectedYear}-${month}-${formattedDay}`;

    onChange(dbDate);
    setCalendarMonth(selectedMonth);
    setCalendarYear(selectedYear);
    setShowCalendar(false);
  };

  const handlePrevMonth = () => {
    if (calendarMonth === 0) {
      setCalendarMonth(11);
      setCalendarYear((y) => y - 1);
    } else {
      setCalendarMonth((m) => m - 1);
    }
  };

  const handleNextMonth = () => {
    if (calendarMonth === 11) {
      setCalendarMonth(0);
      setCalendarYear((y) => y + 1);
    } else {
      setCalendarMonth((m) => m + 1);
    }
  };

  const generateCalendar = useCallback(() => {
    const daysInMonth = new Date(calendarYear, calendarMonth + 1, 0).getDate();
    const firstDay = new Date(calendarYear, calendarMonth, 1).getDay();
    const days: {
      day: number;
      isCurrentMonth: boolean;
      isSelected: boolean;
      isDisabled: boolean;
    }[] = [];

    const prevMonthDays = new Date(calendarYear, calendarMonth, 0).getDate();

    for (let i = firstDay - 1; i >= 0; i--) {
      const d = prevMonthDays - i;
      days.push({
        day: d,
        isCurrentMonth: false,
        isSelected: false,
        isDisabled: false,
      });
    }

    const parts = value ? value.split("-").map(Number) : [];
    const selectedDay = parts[2];
    const selectedMonth = parts[1] ? (parts[1] || 1) - 1 : -1;
    const selectedYear = parts[0] || new Date().getFullYear();

    for (let i = 1; i <= daysInMonth; i++) {
      days.push({
        day: i,
        isCurrentMonth: true,
        isSelected:
          !!value &&
          i === selectedDay &&
          calendarMonth === selectedMonth &&
          calendarYear === selectedYear,
        isDisabled: false,
      });
    }

    const remainingDays = 42 - days.length;
    for (let i = 1; i <= remainingDays; i++) {
      days.push({
        day: i,
        isCurrentMonth: false,
        isSelected: false,
        isDisabled: false,
      });
    }

    return days;
  }, [calendarYear, calendarMonth, value]);

  const displayText = value
    ? new Date(value).toLocaleDateString()
    : "";

  return (
    <div className="detail-assessment-day-picker-inner">
      <h1>Assessment Day</h1>
      <div className="date-picker-wrapper" ref={assessmentDatePickerRef}>
        <div className="date-input-group">
          <input
            type="text"
            value={displayText}
            placeholder="Select date"
            readOnly
            className="date-input"
            onClick={disabled ? undefined : handleCalendarToggle}
            style={{ cursor: disabled ? "default" : "pointer" }}
            aria-label="Assessment day"
          />
          <button
            type="button"
            className="calendar-btn"
            onClick={handleCalendarToggle}
            disabled={disabled}
            aria-label="Open calendar"
          >
            <Iconography icon="calendar" size={IconographySizes.Small} />
          </button>
        </div>

        {showCalendar && (
          <div className="calendar-popup">
            <div className="calendar-header">
              <button type="button" className="nav-btn" onClick={handlePrevMonth}>
                <Iconography icon="chevronLeft" size={IconographySizes.Small} />
              </button>
              <span className="month-year">
                {getMonthName(calendarMonth)} {calendarYear}
              </span>
              <button type="button" className="nav-btn" onClick={handleNextMonth}>
                <Iconography
                  icon="chevronRight"
                  size={IconographySizes.Small}
                />
              </button>
            </div>
            <div className="calendar-weekdays">
              {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((day) => (
                <div key={day} className="weekday">
                  {day}
                </div>
              ))}
            </div>
            <div className="calendar-days">
              {generateCalendar().map((dayObj, index) => (
                <button
                  key={index}
                  type="button"
                  className={`calendar-day ${
                    !dayObj.isCurrentMonth ? "other-month" : ""
                  } ${dayObj.isSelected ? "selected" : ""} ${
                    dayObj.isDisabled ? "disabled" : ""
                  }`}
                  onClick={() =>
                    handleDateSelect(
                      dayObj.day,
                      dayObj.isCurrentMonth,
                      dayObj.isDisabled
                    )
                  }
                  disabled={dayObj.isDisabled}
                >
                  {dayObj.day}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AssessmentDayPicker;
